import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Tag } from 'lucide-react';
import { useStoryboards } from '../../hooks/useStoryboards';

/* ============================================================
   TagInput — autocomplete combobox (Copied logic from NoteListContent)
   ============================================================ */
const TagInput = ({ tags, selectedTagIds, onChange, onCreateTag, isDarkMode, placeholder }) => {
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = React.useRef(null);

    const normalizedSelected = selectedTagIds.map(Number);
    const suggestions = tags.filter(
        (t) => !normalizedSelected.includes(Number(t.id)) &&
            t.name.toLowerCase().includes(query.toLowerCase().trim())
    );

    const handleSelect = (tagId) => {
        onChange([...selectedTagIds, Number(tagId)]);
        setQuery('');
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleRemove = (tagId) => {
        onChange(selectedTagIds.filter((id) => Number(id) !== Number(tagId)));
    };

    const handleCreate = async () => {
        const name = query.trim();
        if (!name || !onCreateTag) return;
        const result = await onCreateTag({ name });
        if (result) {
            onChange([...selectedTagIds, Number(result.id)]);
            setQuery('');
            setShowSuggestions(false);
        }
    };

    const selectedTags = normalizedSelected.map(id => tags.find(t => Number(t.id) === id)).filter(Boolean);

    return (
        <div className="relative">
            <div className={`flex flex-wrap gap-1.5 p-2 rounded-lg border min-h-[42px] transition-all ${isDarkMode ? 'bg-slate-700/50 border-slate-600 focus-within:border-indigo-500' : 'bg-slate-50 border-slate-200 focus-within:border-indigo-500'}`}>
                {selectedTags.map(t => (
                    <span key={t.id} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-500 text-white text-[10px] font-bold rounded-md">
                        {t.name}
                        <button onClick={() => handleRemove(t.id)} className="hover:text-indigo-200"><X size={10} /></button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder={selectedTags.length === 0 ? (placeholder || "Search or create tags...") : ""}
                    className="flex-1 bg-transparent outline-none text-sm min-w-[120px]"
                    onKeyDown={e => {
                        if (e.key === 'Enter' && query.trim()) {
                            e.preventDefault();
                            if (suggestions.length > 0) handleSelect(suggestions[0].id);
                            else handleCreate();
                        }
                    }}
                />
            </div>
            {showSuggestions && query.trim() && (
                <div className={`absolute z-50 w-full mt-1 rounded-xl border shadow-xl overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    {suggestions.map(t => (
                        <button key={t.id} onClick={() => handleSelect(t.id)} className={`w-full text-left px-4 py-2 text-xs hover:bg-indigo-500 hover:text-white transition-colors`}>{t.name}</button>
                    ))}
                    {suggestions.length === 0 && (
                        <button onClick={handleCreate} className="w-full text-left px-4 py-2 text-xs italic opacity-60 hover:bg-emerald-500 hover:text-white transition-colors">Create "{query}"...</button>
                    )}
                </div>
            )}
        </div>
    );
};

const StoryboardModal = ({ isOpen, onClose, onSave, initialData, isDarkMode, tags, addTag }) => {
    const [formName, setFormName] = useState('');
    const [formTags, setFormTags] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFormName(initialData?.name || '');
            setFormTags(initialData?.tag_ids || initialData?.tags?.map(t => Number(t.id)) || []);
        }
    }, [isOpen, initialData]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!formName.trim() || saving) return;
        setSaving(true);
        await onSave({ name: formName.trim(), tag_ids: formTags });
        setSaving(false);
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 space-y-4 animate-scale-in ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{initialData ? 'Edit Storyboard' : 'New Storyboard'}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100/10 transition-colors"><X size={16} /></button>
                </div>

                <div>
                    <label className="text-[10px] font-medium uppercase tracking-wider opacity-60 mb-1 block">Name</label>
                    <input
                        autoFocus
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder="Enter name..."
                        className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-colors ${isDarkMode ? 'bg-slate-700/50 border-slate-600 focus:border-indigo-500 text-slate-200' : 'bg-slate-50 border-slate-200 focus:border-indigo-500 text-slate-800'}`}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    />
                </div>

                <div>
                    <label className="text-[10px] font-medium uppercase tracking-wider opacity-60 mb-1 block">Tags</label>
                    <TagInput tags={tags} selectedTagIds={formTags} onChange={setFormTags} onCreateTag={addTag} isDarkMode={isDarkMode} />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className={`px-4 py-2 text-xs rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>Cancel</button>
                    <button onClick={handleSubmit} disabled={!formName.trim() || saving} className="px-4 py-2 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-40 shadow-sm">{saving ? 'Saving...' : (initialData ? 'Update' : 'Create')}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default StoryboardModal;
